import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import queryString from 'query-string'
import { deleteLocationSearchParam, getFullPath, getPrimaryLocation, locationObjectToString } from 'lib/location'
import { AuthContext, getOrCreateLoginState, setLoginState } from './Auth'
import cookies from 'lib/cookies'
import { debugLog } from 'lib/console'
import { getLocale } from 'lib/utils/getLocale'

import ActionLogic from 'components/Util/ActionLogic'

import createAndLinkBuyerAccountGql from 'graphql/user/createAndLinkBuyerAccount.gql'
import createAndLinkSellerAccountGql from 'graphql/user/createAndLinkSellerAccount.gql'
import createSignatoryAccountGql from 'graphql/user/createSignatoryAccount.gql'

const AuthLogic = ({
    children,
    onMount,
}) => {
    const [createAndLinkBuyerAccountMutation] = useMutation(createAndLinkBuyerAccountGql)
    const [createAndLinkSellerAccountMutation] = useMutation(createAndLinkSellerAccountGql)
    const [createSignatoryAccountMutation] = useMutation(createSignatoryAccountGql)

    const auth = useContext(AuthContext)

    const { i18n } = useTranslation()

    const localLocation = useLocation()
    const queryParamsLocal = queryString.parse(localLocation.search)
    const location = getPrimaryLocation(localLocation) // resolve primary location in case inside modal
    const queryParams = queryString.parse(location.search)

    const loginStateFromOptions = (options) => {
        const loginState = getOrCreateLoginState()

        let successTarget = options.successTarget || queryParamsLocal.loginSuccessTarget

        if (!successTarget) {
            // by default, always get rid of the checksum when login success as it's not needed for authed sessions
            const successLocation = !options.preserveChecksum
                ? deleteLocationSearchParam(location, 'checksum')
                : location
            successTarget = locationObjectToString(successLocation)
        }

        const failedTarget = options.failedTarget || locationObjectToString(location)

        return {
            ...loginState,
            successTarget,
            failedTarget,
        }
    }

    const login = async (actionLogic, options) => {
        actionLogic.asyncSetState({
            error: false,
            loading: true,
        })

        const loginState = loginStateFromOptions(options)

        const loginOptions = {
            email: options.email,
            language: i18n.language,
            source: options.userSourceLabeling?.source,
            type: options.userSourceLabeling?.type,
            loginState: JSON.stringify(loginState),
        }

        debugLog('auth - login - loginOptions:', JSON.stringify(loginOptions))

        setLoginState(loginState)

        auth.actions.popupLogin(loginOptions)
    }

    const logout = async (actionLogic, onSuccessTarget) => {
        const successTarget = queryParams.logoutSuccessTarget || onSuccessTarget || `${getLocale(i18n.language) || '/'}`
        const failedTarget = locationObjectToString(location)

        cookies.set('logoutSuccessTarget', successTarget, { maxAge: 30 })
        cookies.set('logoutFailedTarget', failedTarget, { maxAge: 30 })

        actionLogic.asyncSetState({
            error: false,
            loading: true,
        })

        await auth.actions.logout()
    }

    const register = async (actionLogic, options) => {
        options = {
            ...options,
        }

        auth.actions.redirectRegister({
            email: options.email,
            source: options.userSourceLabeling.source,
            type: options.userSourceLabeling.type,
            loginState: JSON.stringify(loginStateFromOptions(options)),
        })
    }

    const createAndLinkBuyerAccount = async (actionLogic, bosId, email, password) => {
        return actionLogic.callMutation(createAndLinkBuyerAccountMutation, {
            variables: {
                bosId,
                email,
                password,
            }
        })
    }

    const createAndLinkSellerAccount = async (actionLogic, offerId, userInput) => {
        return actionLogic.callMutation(createAndLinkSellerAccountMutation, {
            variables: {
                offerId,
                input: userInput,
            }
        })
    }

    const createSignatoryAccount = async (actionLogic, bosId, email, password) => {
        return actionLogic.callMutation(createSignatoryAccountMutation, {
            variables: {
                bosId,
                email,
                password,
            }
        })
    }

    const getRenderProps = (actionLogic) => {
        return {
            ...actionLogic,
            login: (...args) => login(actionLogic, ...args),
            logout: (...args) => logout(actionLogic, ...args),
            register: (...args) => register(actionLogic, ...args),
            createAndLinkBuyerAccount: (...args) => createAndLinkBuyerAccount(actionLogic, ...args),
            createAndLinkSellerAccount: (...args) => createAndLinkSellerAccount(actionLogic, ...args),
            createSignatoryAccount: (...args) => createSignatoryAccount(actionLogic, ...args),
            currentLocation: getFullPath(location),
        }
    }

    const handleMount = (actionLogic) => {
        onMount && onMount(getRenderProps(actionLogic))
    }

    return (
        <ActionLogic onMount={handleMount}>
            {(actionLogic) => (
                children ? children(getRenderProps(actionLogic)) : null
            )}
        </ActionLogic>
    )
}

AuthLogic.propTypes = {
    onMount: PropTypes.func,
    preserveChecksum: PropTypes.bool,
}

export default AuthLogic
